import { FormEvent, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/api/client";
import { useAuthStore } from "@/stores/auth";
import { BrandMark, HeroBg } from "@/components/Brand";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const login = useAuthStore((s) => s.login);
  const navigate = useNavigate();
  const [search] = useSearchParams();

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setLoading(true);
    try {
      const r = await api.login(email, password);
      login(r.access_token, r.email, r.is_admin, r.display_name);
      // After login the backend has also set the fintbot_session cookie that
      // gates /docs/. If we got here from a "next=" redirect (anonymous user
      // clicked a chat citation), bounce back to that URL so the deep link
      // works on first try. Otherwise land on the chat home.
      const next = search.get("next");
      if (next && next.startsWith("/")) {
        // For /docs/ deep links a hard nav is required so the browser
        // re-issues the request with the freshly-set cookie.
        if (next.startsWith("/docs/")) {
          window.location.href = next;
        } else {
          navigate(next);
        }
      } else {
        navigate("/");
      }
    } catch (x) {
      setErr((x as Error).message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="relative flex min-h-screen flex-col overflow-hidden bg-bg">
      <HeroBg opacity={0.7} />
      {/* subtle dark vignette for legibility */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "linear-gradient(to right, rgba(10,10,12,0.85) 0%, rgba(10,10,12,0.35) 55%, rgba(10,10,12,0.85) 100%)",
        }}
      />

      <header className="relative z-10 flex items-center justify-center px-6 py-5">
        <BrandMark size="md" />
      </header>

      <main className="relative z-10 flex flex-1 items-center justify-center px-6">
        <div className="grid w-full max-w-6xl grid-cols-1 items-center gap-10 md:grid-cols-2">
          <div>
            <h1 className="font-display text-5xl font-semibold leading-[1.05] tracking-tight md:text-6xl">
              Core Solution
              <br />
              For Your{" "}
              <span className="text-accent">Fintech</span>
              <br />
              Evolution.
            </h1>
            <p className="mt-6 max-w-md text-sm text-ink-muted">
              AI assistant for Temenos Transact (T24). Ask in English or
              Russian — grounded answers with page-level citations from your
              internal documentation.
            </p>
          </div>

          <form
            onSubmit={onSubmit}
            className="w-full max-w-sm justify-self-end rounded-2xl border border-bg-border bg-bg-elevated/80 p-8 shadow-card backdrop-blur"
          >
            <h2 className="text-lg font-semibold">Sign in</h2>
            <p className="mb-6 mt-1 text-xs text-ink-muted">
              Use your team credentials.
            </p>
            <label className="mb-3 block">
              <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-ink-dim">
                Email
              </div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
                required
                className="w-full rounded-md border border-bg-border bg-bg-card px-3 py-2 text-sm text-white"
              />
            </label>
            <label className="mb-4 block">
              <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-ink-dim">
                Password
              </div>
              <div className="relative">
                <input
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full rounded-md border border-bg-border bg-bg-card px-3 py-2 pr-10 text-sm text-white"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((s) => !s)}
                  tabIndex={-1}
                  aria-label={showPw ? "Hide password" : "Show password"}
                  title={showPw ? "Hide password" : "Show password"}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-ink-dim hover:text-white"
                >
                  {showPw ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="h-4 w-4"
                      aria-hidden="true"
                    >
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="h-4 w-4"
                      aria-hidden="true"
                    >
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
            </label>
            {err && (
              <div className="mb-3 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300">
                {err}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-md border border-white/20 bg-white py-2.5 text-sm font-semibold text-bg transition hover:bg-white/90 disabled:opacity-60"
            >
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>
      </main>

      <footer className="relative z-10 px-6 py-4 text-center text-[11px] text-ink-dim">
        FintegratorBot · Internal tool
      </footer>
    </div>
  );
}
